import { normalizeSystemOneParams, systemOneStateFormat } from './systemOne';

const preserveLargeElementIds = (jsonText) =>
  jsonText.replace(/("element_id"\s*:\s*)(\d{16,})/g, '$1"$2"');

export const isJsonTextareaParam = (param) => {
  if (!param || param.type !== 'textarea') {
    return false;
  }
  if (param.name === 'messages') {
    return true;
  }
  const placeholder = (param.placeholder || '').trim();
  const description = (param.description || '').toLowerCase();
  return (
    placeholder.startsWith('{') ||
    placeholder.startsWith('[') ||
    description.includes('json')
  );
};

const parseJsonField = (target, fieldName, expectedType = null, strict = true) => {
  const value = target[fieldName];
  if (typeof value !== 'string') {
    return;
  }
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    delete target[fieldName];
    return;
  }
  try {
    const parsedValue = JSON.parse(preserveLargeElementIds(trimmedValue));
    if (expectedType === 'array' && !Array.isArray(parsedValue)) {
      throw new Error();
    }
    if (
      expectedType === 'object' &&
      (parsedValue === null ||
        Array.isArray(parsedValue) ||
        typeof parsedValue !== 'object')
    ) {
      throw new Error();
    }
    target[fieldName] = parsedValue;
  } catch (error) {
    if (strict) {
      throw new Error(`参数 ${fieldName} 需要是合法的 JSON`);
    }
  }
};

const coerceNumericFields = (target, model) => {
  for (const param of model.extra_params || []) {
    if (
      Array.isArray(param.options) &&
      param.options.length > 0 &&
      param.options.some((opt) => typeof opt.value === 'number')
    ) {
      const value = target[param.name];
      if (value !== undefined && value !== null) {
        const num = Number(value);
        if (!isNaN(num)) {
          target[param.name] = num;
        }
      }
    }
  }

  if (model.base_params) {
    for (const [key, defaultValue] of Object.entries(model.base_params)) {
      if (
        typeof defaultValue === 'number' &&
        target[key] !== undefined &&
        target[key] !== null
      ) {
        const num = Number(target[key]);
        if (!isNaN(num)) {
          target[key] = num;
        }
      }
    }
  }
};

const parseConfiguredJsonFields = (target, model, strict) => {
  for (const param of model.extra_params || []) {
    if (isJsonTextareaParam(param)) {
      parseJsonField(target, param.name, null, strict);
    } else if (
      (param.type === 'image_urls' || param.type === 'file_urls') &&
      target[param.name] !== undefined
    ) {
      parseJsonField(target, param.name, 'array', strict);
    }
  }

  if (model.base_params) {
    for (const [key, defaultValue] of Object.entries(model.base_params)) {
      if (defaultValue !== null && typeof defaultValue === 'object') {
        parseJsonField(
          target,
          key,
          Array.isArray(defaultValue) ? 'array' : 'object',
          strict
        );
      }
    }
  }

  parseJsonField(target, 'images', 'array', strict);
  parseJsonField(target, 'byPass', 'object', strict);
};

const nestFlatByPassFields = (target) => {
  const nestedByPass =
    target.byPass && typeof target.byPass === 'object' && !Array.isArray(target.byPass)
      ? { ...target.byPass }
      : {};

  Object.keys(target).forEach((key) => {
    if (!key.startsWith('byPass.')) {
      return;
    }
    const subKey = key.slice(7);
    const value = target[key];
    if (value === '' || (Array.isArray(value) && value.length === 0)) {
      delete target[key];
      return;
    }
    nestedByPass[subKey] = value;
    delete target[key];
  });

  if (Object.keys(nestedByPass).length > 0) {
    target.byPass = nestedByPass;
  } else {
    delete target.byPass;
  }
};

export const buildInitialParams = (model) => {
  const initialParams = { ...(model?.base_params || {}) };

  if (model?.base_params) {
    Object.entries(model.base_params).forEach(([key, value]) => {
      if (key === 'byPass' && value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([subKey, subValue]) => {
          initialParams[`byPass.${subKey}`] = subValue;
        });
        delete initialParams.byPass;
        return;
      }
      if (value !== null && typeof value === 'object') {
        initialParams[key] = JSON.stringify(value, null, 2);
      }
    });
  }

  (model?.extra_params || []).forEach((param) => {
    if (param.default !== undefined && initialParams[param.name] === undefined) {
      initialParams[param.name] = param.default;
    }
  });

  return initialParams;
};

export const buildParamsFromRequestBody = (requestBody) => {
  const next = {};

  Object.entries(requestBody || {}).forEach(([key, value]) => {
    next[key] =
      value !== null && typeof value === 'object'
        ? JSON.stringify(value, null, 2)
        : value;
  });

  if (requestBody.model === 'jev-1.13.0' || requestBody.model === 'laya') {
    next.state_format = systemOneStateFormat(requestBody.state);
  }
  return next;
};

export const normalizeModelParams = (body, model, options = {}) => {
  if (!model) {
    return { ...body };
  }

  const normalized = { ...body };
  const strict = options.strict !== false;

  if (model.endpoint === '/v1/systemone') {
    return normalizeSystemOneParams(normalized, strict);
  }
  parseConfiguredJsonFields(normalized, model, strict);
  coerceNumericFields(normalized, model);
  nestFlatByPassFields(normalized);

  return normalized;
};
